import { useState } from 'react';
import { Briefcase, Pencil } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useStudentCases, useNoteDelete, useNotesForStudent } from '../../api/hooks';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import { EmptyState, Initials, Badge, Button, Spinner, PagedFooter, PillTabs, TextLink } from '../../components/ui';
import { FaClipboardList, FaTrash } from '../../components/ui/icons';
import ParentTab from './ParentTab';
import { nameOr } from '../../lib/names';
import { caseStatusLabel, prettifySlug } from '../../lib/caseStatus';
import { dayMonth, dayMonthTime, possessive } from './lib/format';
import Box from './components/Box';
import { usePagination } from '../../lib/usePagination';
import AddNoteDialog from './components/AddNoteDialog';
import OpenCaseDialog from '../../components/OpenCaseDialog';

/** Artboard `.case` pill tones: open is red, in progress amber, closed green. */
const CASE_TONE: Record<string, 'danger' | 'warning' | 'success' | 'neutral'> = {
  open: 'danger', escalated: 'danger', in_progress: 'warning', resolved: 'success',
};

interface Props {
  id: number;
  /** First name and gender, for "Add a note about Dawit…" and "His teachers". */
  studentName?: string;
  /** Full name, for the case dialog. */
  fullName?: string;
  gender?: string | null;
  /** The note dialog is open (also opened from the header's "Add note"). */
  noteOpen?: boolean;
  onNoteOpenChange?: (open: boolean) => void;
}

type NoteFilter = 'all' | 'mine' | 'teachers_of_student' | 'counselor_admin_only';
const OPEN_CASE = new Set(['open', 'in_progress', 'escalated']);

export default function CasesNotesTab({ id, studentName, fullName, gender, noteOpen, onNoteOpenChange }: Props) {
  const who = possessive(gender);
  const teachersLabel = who === 'their' ? "Student's teachers" : `${who.charAt(0).toUpperCase()}${who.slice(1)} teachers`;
  const { role, username } = useAuth();
  const cases = useStudentCases(id);
  const notes = useNotesForStudent(id);
  const del = useNoteDelete();
  const [ownNoteOpen, setOwnNoteOpen] = useState(false);
  const noteDialogOpen = noteOpen ?? ownNoteOpen;
  const setNoteDialogOpen = onNoteOpenChange ?? setOwnNoteOpen;
  const [caseOpen, setCaseOpen] = useState(false);
  const [filter, setFilter] = useState<NoteFilter>('all');

  const allCases = cases.data?.cases ?? [];
  const openCases = allCases.filter((c) => OPEN_CASE.has(c.status));
  // Open cases first, then the newest.
  const sortedCases = [...allCases].sort((a, b) =>
    Number(OPEN_CASE.has(b.status)) - Number(OPEN_CASE.has(a.status)) || b.opened_at.localeCompare(a.opened_at));
  const allNotes = notes.data ?? [];
  const isMine = (n: (typeof allNotes)[number]) => !!(username && n.author_username === username);
  const shownNotes = allNotes.filter((n) => filter === 'all' || (filter === 'mine' ? isMine(n) : n.visibility === filter));
  const casePg = usePagination(sortedCases, 5, id);
  const notePg = usePagination(shownNotes, 8, `${id}|${filter}`);
  const canSeeCounsellorNotes = role === 'school_admin' || role === 'counselor';

  if (cases.isLoading || notes.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;

  return (
    <div className="space-y-4">
      {/* What is on record, and the two things staff do here. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-[14px] border border-line bg-surface-2 px-4 py-3">
        <span className="text-sm text-ink">
          <b className="font-mono font-semibold">{openCases.length}</b> open {openCases.length === 1 ? 'case' : 'cases'}
          <span className="text-muted"> of {allCases.length}</span>
        </span>
        <span className="text-sm text-ink">
          <b className="font-mono font-semibold">{allNotes.length}</b> {allNotes.length === 1 ? 'note' : 'notes'}
          {allNotes[0] && <span className="text-muted"> · last {dayMonth(allNotes[0].created_at)}</span>}
        </span>
        <span className="flex-1" />
        <span className="flex flex-wrap gap-2">
          <Button variant="secondary" icon={<Briefcase size={16} strokeWidth={1.8} aria-hidden />} onClick={() => setCaseOpen(true)}>Open a case</Button>
          <Button icon={<Pencil size={16} strokeWidth={1.8} aria-hidden />} onClick={() => setNoteDialogOpen(true)}>Add note</Button>
        </span>
      </div>

      {/* Cases and family side by side, then the notes across the full width. */}
      <div className="grid items-start gap-4 md:grid-cols-2">
          <Box title="Cases" help="Intervention cases opened for this student, with their status. Click one to see its timeline." actions={<TextLink to="/cases">All cases →</TextLink>}>
            {allCases.length === 0 ? (
              <EmptyState compact icon={<FaClipboardList />} title="No cases" body="Open one when the school starts supporting this student." />
            ) : (
              <ul className="space-y-2">
                {casePg.pageItems.map((c) => (
                  <li key={c.id}>
                    <Link to={`/cases/${c.id}`}
                      className="flex items-center gap-3 rounded-[12px] border border-line px-3 py-2.5 text-ink transition-colors hover:bg-surface-2">
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{prettifySlug(c.category)} · #{c.id}</span>
                        <span className="block truncate text-xs text-muted">
                          Opened {dayMonth(c.opened_at)}{c.closed_at && ` · closed ${dayMonth(c.closed_at)}`}
                          {c.outcome && ` · ${prettifySlug(c.outcome)}`}
                        </span>
                      </span>
                      <Badge tone={CASE_TONE[c.status] ?? 'neutral'} size="sm">{caseStatusLabel(c.status)}</Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <PagedFooter className="mt-3" label="Cases pagination" {...casePg} onPage={casePg.setPage} />
          </Box>
          <ParentTab id={id} studentName={studentName} />
      </div>

        <Box title="Notes" help="Short notes staff leave about the student. Visibility is either the student's teachers or counsellors and admins only.">
          {allNotes.length > 0 && (
            <div className="mb-3">
              <PillTabs dense label="Show notes" value={filter} onChange={(k) => setFilter(k as NoteFilter)}
                options={[
                  { key: 'all', label: 'All' },
                  { key: 'mine', label: 'Mine' },
                  ...(canSeeCounsellorNotes ? [
                    { key: 'teachers_of_student', label: teachersLabel },
                    { key: 'counselor_admin_only', label: 'Counsellors only' },
                  ] : []),
                ]} />
            </div>
          )}
          {shownNotes.length === 0 ? (
            <EmptyState compact icon={<FaClipboardList />} title={allNotes.length === 0 ? 'No notes yet' : 'No notes match'}
              body={allNotes.length === 0 ? `Notes you add about ${studentName || 'this student'} appear here.` : undefined}
              action={allNotes.length === 0 ? <Button variant="secondary" onClick={() => setNoteDialogOpen(true)}>Add the first note</Button> : undefined} />
          ) : (
            <ul>
              {notePg.pageItems.map((n) => {
                // Spec §6: author OR admin only. Backend enforces authoritatively;
                // this is the matching UI affordance.
                const canDelete = role === 'school_admin' || isMine(n);
                const author = nameOr(n.author_name, n.author_username);
                return (
                  <li key={n.id} className="flex gap-3 border-t border-hairline py-3.5 first:border-t-0 first:pt-0">
                    <Initials name={nameOr(n.author_name, n.author_username, '?')} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <b className="font-semibold text-ink">{author}</b>
                        <span className="text-xs text-muted">{dayMonthTime(n.created_at)}</span>
                        {n.pinned && <Badge tone="info" size="sm">Pinned</Badge>}
                        <Badge tone={n.visibility === 'counselor_admin_only' ? 'warning' : 'neutral'} size="sm" className="ml-auto">
                          {n.visibility === 'counselor_admin_only' ? 'Counsellors and admins' : teachersLabel}
                        </Badge>
                      </div>
                      <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-ink">{n.body}</p>
                    </div>
                    {canDelete && (
                      <button type="button" onClick={() => del.mutate(n.id)}
                              className="flex min-h-[36px] min-w-[36px] flex-none items-center justify-center rounded-lg text-muted transition-colors hover:text-danger-ink"
                              title="Delete note" aria-label="Delete note">
                        <FaTrash />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <PagedFooter className="mt-3.5" label="Notes pagination" {...notePg} onPage={notePg.setPage} />
        </Box>

      <AddNoteDialog isOpen={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} studentId={id}
        studentName={studentName} teachersLabel={teachersLabel} />
      <OpenCaseDialog isOpen={caseOpen} onClose={() => setCaseOpen(false)}
        student={{ id, name: fullName || studentName || 'This student' }} />
    </div>
  );
}
